/**
 * Skeleton table component for table loading states
 * @module components/common/SkeletonTable
 */

import React from 'react'
import { SkeletonText, SkeletonRounded } from './Skeleton'

export interface SkeletonTableProps {
  /**
   * Number of rows to display
   */
  rows?: number

  /**
   * Number of columns to display
   */
  columns?: number

  /**
   * Show table header skeleton
   */
  showHeader?: boolean
}

/**
 * Skeleton table component for table loading states
 *
 * Displays skeleton table with specified rows and columns
 */
export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 10,
  columns = 5,
  showHeader = true
}) => {
  return (
    <div className="w-full overflow-hidden rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full">
          {showHeader && (
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {Array.from({ length: columns }).map((_, index) => (
                  <th key={`header-${index}`} className="px-4 py-3 text-left">
                    <SkeletonText width="80%" height={16} />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-border">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={`row-${rowIndex}`} className="hover:bg-muted/50">
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <td key={`cell-${rowIndex}-${colIndex}`} className="px-4 py-3">
                    {colIndex === columns - 1 ? (
                      // Last column: action button skeleton
                      <SkeletonRounded width={80} height={32} />
                    ) : colIndex === 0 ? (
                      // First column: typically an ID or name (wider)
                      <SkeletonText width="90%" height={16} />
                    ) : (
                      // Other columns: regular text
                      <SkeletonText width="70%" height={16} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
